import * as React from "react";
import { type CSSProperties, type ReactNode, useContext } from "react";
import classNames from "classnames";
import { createSelector } from "reselect";
import { Collapse } from "~/common/mui";
import { IconType } from "../icon/IconType";
import { Icon } from "../icon/Icon";

type Themes = "stripes" | "separatorsTop" | "separators" | "none";

interface ContextProps {
  noPadding: boolean;
  columnFlex: string[][] | string[];
  theme: Themes;
}

const TableContext = React.createContext<ContextProps>({
  noPadding: false,
  columnFlex: [],
  theme: "stripes",
});
const HeaderContext = React.createContext<boolean>(false);

interface DataTableProps {
  header?: ReactNode;
  className?: string;
  noPadding?: boolean;
  columnFlex?: string[][] | string[];
  theme?: Themes;
  children?: ReactNode;
}
const EMPTY_LIST: string[] = [];
const providerDataSelector = createSelector(
  (props: DataTableProps) => !!props.noPadding,
  (props: DataTableProps) => props.columnFlex || EMPTY_LIST,
  (props: DataTableProps) => props.theme || "stripes",
  (noPadding, columnFlex, theme) => ({ noPadding, columnFlex, theme }),
);

/**
 *
 * @deprecated
 */
export const Table = (props: DataTableProps) => {
  const { className, children, header } = props;
  return (
    <TableContext.Provider value={providerDataSelector(props)}>
      <div className={classNames("scha__data-table", className)}>
        {header && (
          <div className={"scha__data-table__header"}>
            <HeaderContext.Provider value={true}>{header}</HeaderContext.Provider>
          </div>
        )}
        <div className="scha__data-table__body">{children}</div>
      </div>
    </TableContext.Provider>
  );
};

export const TableItem: React.FC<{
  className?: string;
  style?: CSSProperties;
  isOdd?: boolean;
  children?: ReactNode;
}> = (props) => {
  const { className, isOdd, ...rest } = props;

  const contextProps = useContext(TableContext);
  const themeClass = `scha__data-table__item--${contextProps.theme}`;
  const oddEvenCls =
    isOdd !== undefined ? (isOdd ? "scha__data-table__item--odd-row" : "scha__data-table__item--even-row") : "";

  return <div className={classNames("scha__data-table__item", themeClass, oddEvenCls, className)} {...rest} />;
};

interface ExpandableTableItemProps {
  header: ReactNode;
  isExpanded?: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  children?: ReactNode;
}

export const ExpandableTableItem: React.FC<ExpandableTableItemProps & JSX.IntrinsicElements["div"]> = (props) => {
  const { children, className, isExpanded, header, ...divProps } = props;

  const contextProps = useContext(TableContext);
  const themeClass = `scha__data-table__item--${contextProps.theme}`;

  const cls = classNames(
    "scha__data-table__item scha__data-table__item--expandable",
    themeClass,
    isExpanded && "expanded",
    className,
  );

  const cardCls = classNames("scha__data-table__item__card", isExpanded && "expanded");

  return (
    <div className={cls} {...divProps}>
      <div className={cardCls}>
        {header}
        <Collapse in={isExpanded}>{children}</Collapse>
      </div>
    </div>
  );
};

interface ExpandableTableItemIndicatorProps {
  className?: string;
  isExpanded?: boolean;
}

export const ExpandableTableItemIndicator: React.FC<ExpandableTableItemIndicatorProps> = (props) => {
  const { isExpanded, className } = props;
  return (
    <Icon
      className={classNames("scha__data-table__expandable-icon", className)}
      type={isExpanded ? IconType.ChevronUp : IconType.ChevronDown}
    />
  );
};

interface CellRowProps {
  className?: string;
  noPadding?: boolean;
  columnFlex?: string[][] | string[];
  children?: ReactNode;
}

export const CellRow: React.FC<CellRowProps> = (props) => {
  const { className, children } = props;

  const contextProps = useContext(TableContext);
  const columnFlex = props.columnFlex || contextProps.columnFlex;
  const noPadding = props.noPadding !== undefined ? props.noPadding : contextProps.noPadding;
  const themeClass = `scha__data-table__cell-row--${contextProps.theme}`;

  const isHeader = useContext(HeaderContext);

  return (
    <div className={classNames("scha__data-table__cell-row", themeClass, className)}>
      {React.Children.map(children, (child, index) => {
        if (child && typeof child === "object" && "type" in child && child.type === Cell) {
          return React.cloneElement(child, {
            columnFlex: columnFlex[index],
            noPadding,
            isHeader,
            ...child.props,
          });
        }
        return child;
      })}
    </div>
  );
};

interface CellProps {
  className?: string;
  noPadding?: boolean;
  columnFlex?: string[] | string;
  isHeader?: boolean;
  children?: ReactNode;
}

const convertColumnFlexToStyle = (columnFlex?: string | string[]) => {
  if (!columnFlex) {
    return undefined;
  }
  if (!Array.isArray(columnFlex)) {
    return { flex: columnFlex };
  }
  if (columnFlex.length === 3) {
    return {
      flexGrow: columnFlex[0],
      flexShrink: columnFlex[1],
      flexBasis: columnFlex[2],
    };
  }
  return { flex: columnFlex.join(" ") };
};

export const Cell: React.FC<CellProps> = (props) => {
  // const {className, noPadding, columnFlex, children, isHeader} = props;
  const { className, noPadding, columnFlex, children } = props;

  const cls = classNames(
    {
      "scha__data-table__cell": true,
      "scha__data-table__cell--no-padding": noPadding,
    },
    className,
  );

  const style = convertColumnFlexToStyle(columnFlex);

  // return React.createElement(isHeader ? 'th' : 'td', {className: cls, style}, children);
  return React.createElement("div", { className: cls, style }, children);
};

export const generateFlexFromPercentages = (percentages: number[]) =>
  percentages.map((value) => ["0", "0", `${value * 100}%`]);

export const generateNestedFlexFromPercentages = (
  percentages: number[],
  paddingLeft: number,
  paddingRight: number = paddingLeft,
) =>
  percentages.map((value, index) => {
    const totalPadding = paddingLeft + paddingRight;
    const isFirst = index === 0;
    if (isFirst) {
      return ["0", "0", `calc((100% + ${totalPadding}px) * ${value} - ${paddingLeft}px)`];
    }
    const isLast = index === percentages.length - 1;
    if (isLast) {
      return ["0", "0", `calc((100% + ${totalPadding}px) * ${value} - ${paddingRight}px)`];
    }
    return ["0", "0", `calc((100% + ${totalPadding}px) * ${value}`];
  });

const DataTable = {
  Table,
  TableItem,
  ExpandableTableItem,
  ExpandableTableItemIndicator,
  CellRow,
  Cell,
  generateFlexFromPercentages,
  generateNestedFlexFromPercentages,
};

export default DataTable;
